/** @jsxImportSource @emotion/react */
import { css, keyframes } from "@emotion/react";

/* ------------------------------------------------------------------ */
/* 1.  Structural interfaces                                           */
/* ------------------------------------------------------------------ */
export interface Colors {
  background: string;
  cardBackground: string;
  inputBackground: string;
  border: string;
  primary: string;
  primaryDark: string;
  textPrimary: string;
  textSecondary: string;
  error: string;
}

export interface Fonts {
  primary: string;
  secondary: string;
}

export interface AppTheme {
  colors: Colors;
  fonts: Fonts;
}
/* ------------------------------------------------------------------ */
/* 2.  Concrete palette objects                                        */
/*     – keep literal intellisense via `satisfies`                     */
/* ------------------------------------------------------------------ */
export const lightTheme = {
  colors: {
    background: "#E6EBFF80",
    cardBackground: "#f8f9fa",
    inputBackground: "#ffffff",
    border: "#ced4da",
    primary: "#0057ff",
    primaryDark: "#0046cc",
    textPrimary: "#212529",
    textSecondary: "#6c757d",
    error: "#d9534f",
  },
  fonts: {
    primary: `'Literata', serif`,
    secondary: `'Inter', sans-serif`,
  },
} as const satisfies AppTheme;

export const darkTheme = {
  colors: {
    background: "#10243E", // Primary dark blue as main background
    cardBackground: "#22416A", // Slightly lighter for cards to create subtle contrast
    inputBackground: "#22416A", // Input fields slightly lighter than background
    border: "#5E84ED", // Accent color for active or focused elements
    primary: "#5E84ED", // Accent color for buttons or interactive elements
    primaryDark: "#22416A", // Secondary interactive state or hovered buttons
    textPrimary: "#EAEAEA", // Main body text for readability
    textSecondary: "#5E84ED", // Secondary emphasis text, links, or highlighted elements
    error: "#EB9881", // Errors/warnings, clear visibility against dark background
  },
  fonts: {
    primary: `'Literata', serif`,
    secondary: `'Inter', sans-serif`,
  },
} as const satisfies AppTheme;

/* Useful union for runtime comparisons */
export type ThemeMode = typeof lightTheme | typeof darkTheme;

/* ------------------------------------------------------------------ */
/* 3.  Emotion theme augmentation                                      */
/* ------------------------------------------------------------------ */
declare module "@emotion/react" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface Theme extends AppTheme {}
}

/* ------------------------------------------------------------------ */
/* 4.  Global styles helper                                            */
/* ------------------------------------------------------------------ */

export const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const GlobalStyles = (theme: AppTheme) =>
  css`
    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    * {
      -webkit-tap-highlight-color: transparent;
      -webkit-touch-callout: none;
    }

    html {
      scroll-behavior: smooth;
      touch-action: manipulation;
    }

    body {
      margin: 0;
      font-family: ${theme.fonts.primary};
      background: white;
      color: ${theme.colors.textPrimary};
      transition: background 0.3s ease, color 0.3s ease;
      animation: ${fadeIn} 0.6s ease-in-out;
      overscroll-behavior: auto;
      touch-action: manipulation;
    }

    button, a, input, textarea {
      -webkit-tap-highlight-color: transparent;
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      user-select: none;
    }
    .snap-section {
      scroll-snap-align: start;
      scroll-margin-top: 80px; /* Offset for sticky header */
    }

    [data-fade] {
      opacity: 0;
      transform: translateY(12px);
      animation: ${fadeIn} 0.6s ease-in-out forwards;
      will-change: opacity, transform;
    }

    [data-fade="delay-1"] {
      animation-delay: 0.3s;
    }

    [data-fade="delay-2"] {
      animation-delay: 0.6s;
    }

    [data-fade="delay-3"] {
      animation-delay: 0.9s;
    }
  `;
